/**
 * Document Delivery Service — API client for eSENTRAL document delivery history and re-sending
 */
import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';
import type {
  DocumentDeliveryLog,
  DocumentDeliverySearchParams,
  EsentralResult
} from '../types/documentDelivery';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class DocumentDeliveryService {

  async search(companyNo: string, params: DocumentDeliverySearchParams = {}): Promise<DocumentDeliveryLog[]> {
    const queryParams = new URLSearchParams();
    if (params.documentType) queryParams.set('documentType', params.documentType);
    if (params.status) queryParams.set('status', params.status);
    if (params.fromDate) queryParams.set('fromDate', params.fromDate);
    if (params.toDate) queryParams.set('toDate', params.toDate);
    if (params.limit) queryParams.set('limit', params.limit.toString());
    if (params.offset) queryParams.set('offset', params.offset.toString());

    const url = `${API_BASE_URL}/${companyNo}/DocumentDelivery/history?${queryParams.toString()}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) throw new Error(`Failed to search delivery history: ${response.statusText}`);
    return response.json();
  }

  async searchCount(companyNo: string, params: DocumentDeliverySearchParams = {}): Promise<number> {
    const queryParams = new URLSearchParams();
    if (params.documentType) queryParams.set('documentType', params.documentType);
    if (params.status) queryParams.set('status', params.status);
    if (params.fromDate) queryParams.set('fromDate', params.fromDate);
    if (params.toDate) queryParams.set('toDate', params.toDate);

    const url = `${API_BASE_URL}/${companyNo}/DocumentDelivery/history/count?${queryParams.toString()}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) throw new Error(`Failed to get delivery count: ${response.statusText}`);
    const data = await response.json();
    return data.count;
  }

  async getById(companyNo: string, logId: number): Promise<DocumentDeliveryLog> {
    const url = `${API_BASE_URL}/${companyNo}/DocumentDelivery/${logId}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) throw new Error(`Failed to get delivery log: ${response.statusText}`);
    return response.json();
  }

  async resend(companyNo: string, logId: number): Promise<EsentralResult> {
    const url = `${API_BASE_URL}/${companyNo}/DocumentDelivery/resend`;
    const response = await AuthService.authenticatedPost(url, { logId });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      return {
        success: false,
        errorMessage: extractApiError(errorData, response, 'Document delivery resend failed'),
        httpStatusCode: response.status,
      };
    }
    return response.json();
  }
}

export const documentDeliveryService = new DocumentDeliveryService();
